const COMPANY_ACTIVATION_INTEREST_STORAGE_KEY = 'vt-company-activation-interest';
const COMPANY_ACTIVATION_INTEREST_DRAFT_STORAGE_KEY = 'vt-company-activation-interest-draft';

export interface CompanyActivationInterest {
  companyId: string;
  companyName: string;
  customerName: string;
  customerPhoneNumber: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyActivationInterestDraft {
  companyId: string;
  customerName: string;
  customerPhoneNumber: string;
  isExpanded: boolean;
  updatedAt: string;
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function readStoredEntries() {
  if (!isBrowser()) {
    return [] as CompanyActivationInterest[];
  }

  try {
    const rawValue = window.localStorage.getItem(COMPANY_ACTIVATION_INTEREST_STORAGE_KEY);

    if (!rawValue) {
      return [] as CompanyActivationInterest[];
    }

    const parsedValue = JSON.parse(rawValue);

    return Array.isArray(parsedValue) ? (parsedValue as CompanyActivationInterest[]) : [];
  } catch {
    return [] as CompanyActivationInterest[];
  }
}

function writeStoredEntries(entries: CompanyActivationInterest[]) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(COMPANY_ACTIVATION_INTEREST_STORAGE_KEY, JSON.stringify(entries));
}

function readStoredDraftEntries() {
  if (!canUseSessionStorage()) {
    return [] as CompanyActivationInterestDraft[];
  }

  try {
    const rawValue = window.sessionStorage.getItem(COMPANY_ACTIVATION_INTEREST_DRAFT_STORAGE_KEY);

    if (!rawValue) {
      return [] as CompanyActivationInterestDraft[];
    }

    const parsedValue = JSON.parse(rawValue);

    return Array.isArray(parsedValue) ? (parsedValue as CompanyActivationInterestDraft[]) : [];
  } catch {
    return [] as CompanyActivationInterestDraft[];
  }
}

function writeStoredDraftEntries(entries: CompanyActivationInterestDraft[]) {
  if (!canUseSessionStorage()) {
    return;
  }

  window.sessionStorage.setItem(COMPANY_ACTIVATION_INTEREST_DRAFT_STORAGE_KEY, JSON.stringify(entries));
}

export function getCompanyActivationInterest(companyId: string) {
  return readStoredEntries().find((entry) => entry.companyId === companyId) ?? null;
}

export function getCompanyActivationInterestDraft(companyId: string) {
  return readStoredDraftEntries().find((entry) => entry.companyId === companyId) ?? null;
}

export function saveCompanyActivationInterest(
  entry: Pick<CompanyActivationInterest, 'companyId' | 'companyName' | 'customerName' | 'customerPhoneNumber'>,
) {
  const entries = readStoredEntries();
  const now = new Date().toISOString();
  const entryIndex = entries.findIndex((storedEntry) => storedEntry.companyId === entry.companyId);
  const nextEntry: CompanyActivationInterest = {
    ...entry,
    createdAt: entryIndex >= 0 ? entries[entryIndex].createdAt : now,
    updatedAt: now,
  };

  if (entryIndex >= 0) {
    entries[entryIndex] = nextEntry;
  } else {
    entries.unshift(nextEntry);
  }

  writeStoredEntries(entries);

  return nextEntry;
}

export function saveCompanyActivationInterestDraft(
  entry: Pick<CompanyActivationInterestDraft, 'companyId' | 'customerName' | 'customerPhoneNumber' | 'isExpanded'>,
) {
  const entries = readStoredDraftEntries();
  const now = new Date().toISOString();
  const entryIndex = entries.findIndex((storedEntry) => storedEntry.companyId === entry.companyId);
  const nextEntry: CompanyActivationInterestDraft = {
    ...entry,
    updatedAt: now,
  };

  if (entryIndex >= 0) {
    entries[entryIndex] = nextEntry;
  } else {
    entries.unshift(nextEntry);
  }

  writeStoredDraftEntries(entries);

  return nextEntry;
}

export function clearCompanyActivationInterestDraft(companyId: string) {
  const nextEntries = readStoredDraftEntries().filter((entry) => entry.companyId !== companyId);

  writeStoredDraftEntries(nextEntries);
}
